
import img0 from './OIDC_New_Application.png';
import img1 from './OIDC_Configuration.png';
import img2 from './OIDC_Configuration_Step1.png';
import img3 from './OIDC_Configuration_Step2.png';
import img4 from './App_Role_Configuration.png';
import img5 from './OIDC_OnData_Configuration.png';
import img6 from './SAML_New_Application.png';
import img7 from './SAML_Configuration.png';
import img8 from './SAML_Configuration_Step1.png';
import img9 from './SAML_Configuration_Step2.png';
import img10 from './App_Role_Configuration.png';
import img11 from './SAML_OnData_Configuration.png';
  
const content = `# Microsoft Entra ID Integration Guide

OnData supports the integration with Identity and Access Management (IAM) systems for user authentication and authorization, based on the industry standard 
protocols, including SAML 2.0 and OpenID Connect (OIDC) protocols. The purpose of this document is to detail the process of configuring OnData and Microsoft Entra ID 
(formerly Azure Active Directory) using either the SAML 2.0 protocol or the OpenID Connect protocol, so that the users can login to OnData with their Microsoft
Entra ID accounts.

## Microsoft Entra ID Integration with OIDC

### 1. Prerequisite
a. An Entra ID user account with at least one of the following roles: Global Administrator, Cloud Application Administrator, or Application Administrator  
b. An OnData account with the System Administrator role  

### 2. Entra ID Configuration
a. Login to the Azure Portal, and select Microsoft Entra ID  
b. Select “App registration” from the left menu  
c. Select “New registration” from the top menu  

![Create New Application](${ img0 })

d. Use “OnData Portal” as the “Name” of the application, choose the option of “Accounts in this organizational directory only” for the “Supported account types”, 
and configure the “Redirect URL” with this value: https://api.ondatahub.com/users/api/sso/oidc/consumer  

![OIDC Configuration](${ img1 })

e. Click on “Register” to register this application  
f. Select “Overview” from the left menu:   
- The value of the “Application (client) ID” is needed to configure the “Client ID” field at the OnData side  
- The value of the “Directory (tenant) ID” is needed to configure the “Well Known Configuration URL” field at the OnData side  
  
![OIDC Configuration Step 1](${ img2 })  
  
g. Select “Certificates & secrets” from the left menu  
h. Select the “Client secrets” tab and click on “New client secret”  
i. Add a client secret and copy the secret value. Note: You **must copy the secret value** when the client secret is first created. Client secret values 
cannot be viewed, except for immediately after creation. Be sure to save the secret when created before leaving the page  
  
![OIDC Configuration Step 2](${ img3 })  
  
j. Select "App roles" from the left menu  
k. Click on "Create app role" to create the OnData "System Administrator" app role  
l. Use "System Administrator" as the "Display name", and select the "Users/Groups" option under the "Allowed member types". Enter the role value of 
"SystemAdministrator", which should match the role value of the System Administrator role at the OnData Portal. Enter a description and select 
the "Apply" button to create the role  
  
![App Role Configuration](${ img4 })  
  
m. You may follow the same process to create all other app roles that match what you have at the OnData Portal, including the custom roles you have created. 
But you may choose to do that later  
n. Go back to the Entra ID home page, select “Enterprise applications” on the left menu  
o. Select the “OnData Portal” application, and on the “Overview” page, select “Assign users and groups” under the tile named “1.  Assign users and groups”  
p. Click on "Add user/group" to add one or more System Administrator users. Select one or more users from Entra ID, and select the System Administrator role, 
then click on the "Assign" button to complete the assignment  
  
### 3. OnData Configuration
a. Login to the OnData Portal as a System Administrator, select the Settings icon from the top right corner, and select Organization from the dropdown menu  
b. Select the SSO Configuration tab  
c. Toggle the switch to “Enable Single Sign-ON (SSO)”  
d. Under the Protocol choice, select the “OpenID Connect” option  
e. Fill out the “Well Known Configuration URL” field with either of the following values:  
- For Azure Global Cloud: https://login.microsoftonline.com/{tenant ID}/v2.0/  
- For Azure Government Cloud: https://login.microsoftonline.us/{tenant ID}/v2.0/  

Please replace {tenant ID} with the value of the “Directory (tenant) ID” field from the application overview page at the Azure Portal  
  
f. Fill out the “Client ID” field with the value of the “Application (client) ID” field from the application overview page  
g. Fill out the “Client Secret” field with the Secret Value copied from the Certificates & secrets page when the client secret is created  
h. Fill out the "Logout Redirect URL with https://us.ondatahub.com  
i. Fill out the "On Logout, Sign Out IdP at URL" with either of the following values:  
- For Azure Global Cloud: https://login.microsoftonline.com/{tenant ID}/oauth2/v2.0/logout  
- For Azure Government Cloud: https://login.microsoftonline.us/{tenant ID}/oauth2/v2.0/logout  

Please replace {tenant ID} with the value of the “Directory (tenant) ID” field from the application overview page  
  
j. Click on the “Update SSO Configuration” button to update the SSO settings  
k. Log out of OnData and login again with a user’s organization email address who has been granted access to the OnData Portal application in Step 2 – Entra ID Configuration above  
l. You should be redirected to the Entra ID for login. After a successful login, you will be redirected back to the OnData Portal  

![OIDC OnData Configuration](${ img5 })


## Microsoft Entra ID Integration with SAML

### 1. Prerequisite
a. An Entra ID user account with at least one of the following roles: Global Administrator, Cloud Application Administrator, or Application Administrator  
b. An OnData account with the System Administrator role  

### 2. Entra ID Configuration
a. Login to the Azure Portal, and select Microsoft Entra ID  
b. Select “All applications” from the left menu  
c. Select “New application” from the top menu  

![Create New Application](${ img6 })

d. Select “Create your own application” from the top menu  
e. Use “OnData Portal” as the name of the application. Use the default option of “Integrate any other application you don’t find in the gallery (Non-gallery)”. 
Then click on the “Create” button to create the application  
f. After the application is created, on the application overview page, select “Get started” under the tile “2. Set up single sign on”  
g. Select “SAML” as the single sign-on method  
h. The “Set up Single Sign-On with SAML” page will be displayed, as illustrated below  

![SAML Configuration](${ img7 })

i. Click on “Edit” on Step 1 – “Basic SAML Configuration”  
j. Click on “Add identifier” and enter the value of “users.ondatahub.com”  
k. Click on “Add reply URL” and enter the OnData SAML Assertion Consumer Service URL: https://api.ondatahub.com/users/api/sso/saml/consumer  
l. Leave the other optional fields empty, click on the “Save” button, and close the “Basic SAML Configuration” pop-up

![SAML Configuration Step 1](${ img8 })

m. Click on “Edit” on Step 2 – “Attributes & Claims”  
n. Click on “Add new claim” at the top menu, use “email” as the “Name” of the attribute, use the default value of “Attribute” for the “Source” field, 
and use “user.mail” for the “Source attribute” field. Leave all other fields with default values. Click on “Save” to save this attribute, and close the 
“Manage claim” pop-up and the “Attributes & Claims” pop-up  

![SAML Configuration Step 2](${ img9 })

o. Under Step 3 – “SAML Signing Certificate”, download the “Certificate (Base64)” file. This certificate file is needed to configure the 
“Identity Provider X.509 Certificate” field at the OnData side  
p. Under Step 4 – “Set up OnData Portal”:  
- The value of the “Login URL” is needed to configure the “Sign In URL” field at the OnData side  
- The value of the “Azure AD Identifier” is needed to configure the “Service Provider Entity ID” field at the OnData side  
- The value of the “Logout URL” is needed to configure the “On Logout, Sign Out IdP at URL” field at the OnData side
    
q. Select "App roles" from the left menu  
r. Click on "Create app role" to create the OnData "System Administrator" app role  
s. Use "System Administrator" as the "Display name", and select the "Users/Groups" option under the "Allowed member types". Enter the role value of 
"SystemAdministrator", which should match the role value of the System Administrator role at the OnData Portal. Enter a description and select 
the "Apply" button to create the role  
  
![App Role Configuration](${ img4 })  
  
t. You may follow the same process to create all other app roles that match what you have at the OnData Portal, including the custom roles you have created. 
But you may choose to do that later  
u. Go back to the Entra ID home page, select “Enterprise applications” on the left menu  
v. Select the “OnData Portal” application, and on the “Overview” page, select “Assign users and groups” under the tile named “1.  Assign users and groups”  
w. Click on "Add user/group" to add one or more System Administrator users. Select one or more users from Entra ID, and select the System Administrator role, 
then click on the "Assign" button to complete the assignment  
  
### 3. OnData Configuration
a. Login to the OnData Portal as a System Administrator, select the Settings icon from the top right corner, and select Organization from the dropdown menu  
b. Select the SSO Configuration tab  
c. Toggle the switch to “Enable Single Sign-ON (SSO)”  
d. Under the Protocol choice, select the “SAML 2.0” option  
e. Fill out the “Service Provider Entity ID” field with the value of the “Azure AD Identifier” field from Azure Portal  
f. Fill out the “Sign In URL” field with the value of the “Login URL” field from Azure Portal  
g. Upload the “Certificate (Base64)” file downloaded from Azure AD to the “Identity Provider X.509 Certificate” field  
h. Fill out the “On Logout, Sign Out IdP at URL” field with the value of the “Logout URL” field from Azure AD, as illustrated below  
i. Click on the “Update SSO Configuration” button to update the SSO settings  
j. Log out of OnData and login again with a user’s organization email address who has been granted access to the OnData Portal application in 
Step 2 – Entra ID Configuration above  
k. You should be redirected to the Entra ID for login. After a successful login, you will be redirected back to the OnData Portal  

![SAML OnData Configuration](${ img11 })
`
  
export default content;
